import { getMessaging, getToken, onMessage, Messaging } from 'firebase/messaging';
import { doc, updateDoc, arrayUnion, arrayRemove, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { getApp } from 'firebase/app';

// VAPID Key pública de Firebase Cloud Messaging
const VAPID_KEY = 'BKKK6fuiCMHVuPSKPheNUZwbW8qPEHZGzq0D8eKoLTM1ukPUBIQpBFAD2iyGBS3SRjDxf8XPNP8uzkyUnMzAvxo';

let messaging: Messaging | null = null;

/**
 * Inicializa Firebase Messaging
 */
const initializeMessaging = async (): Promise<Messaging | null> => {
  if (messaging) return messaging;
  
  try {
    // Verificar que el navegador soporta notificaciones
    if (!('Notification' in window)) {
      console.warn('⚠️ Este navegador no soporta notificaciones');
      return null;
    }
    
    // Verificar que tenemos Service Worker
    if (!('serviceWorker' in navigator)) {
      console.warn('⚠️ Service Worker no soportado');
      return null;
    }
    
    const app = getApp();
    messaging = getMessaging(app);
    console.log('✅ Firebase Messaging inicializado');
    return messaging;
  } catch (error) {
    console.error('❌ Error inicializando Firebase Messaging:', error);
    return null;
  }
};

/**
 * Verifica si las notificaciones están soportadas
 */
export const isPushSupported = (): boolean => {
  return 'Notification' in window && 
         'serviceWorker' in navigator && 
         'PushManager' in window;
};

/**
 * Obtiene el estado actual de los permisos de notificación
 */
export const getNotificationPermission = (): NotificationPermission => {
  if (!('Notification' in window)) return 'denied';
  return Notification.permission;
};

/**
 * Solicita permiso para notificaciones y obtiene el token FCM
 */
export const requestNotificationPermission = async (userId: string, businessId: string): Promise<string | null> => {
  console.log('🔔 [FCM] Iniciando requestNotificationPermission...', { userId, businessId });
  
  try {
    if (!isPushSupported()) {
      console.warn('⚠️ [FCM] Push notifications no soportadas');
      return null;
    }
    console.log('✅ [FCM] Push soportado');
    
    // Solicitar permiso
    console.log('🔔 [FCM] Solicitando permiso...');
    const permission = await Notification.requestPermission();
    console.log('📱 [FCM] Permiso de notificación:', permission);
    
    if (permission !== 'granted') {
      console.warn('⚠️ [FCM] Permiso de notificación denegado');
      return null;
    }
    
    // Inicializar messaging
    console.log('🔔 [FCM] Inicializando messaging...');
    const msg = await initializeMessaging();
    if (!msg) {
      console.error('❌ [FCM] No se pudo inicializar messaging');
      return null;
    }
    console.log('✅ [FCM] Messaging inicializado');
    
    // Registrar el Service Worker para FCM
    console.log('🔔 [FCM] Registrando Service Worker...');
    const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
    console.log('✅ [FCM] Service Worker registrado:', registration.scope);
    
    // Esperar a que el SW esté activo
    await navigator.serviceWorker.ready;
    console.log('✅ [FCM] Service Worker listo');
    
    // Obtener token FCM
    console.log('🔔 [FCM] VAPID_KEY:', VAPID_KEY ? VAPID_KEY.substring(0, 20) + '...' : 'NO CONFIGURADA');
    
    if (!VAPID_KEY) {
      console.error('❌ [FCM] VAPID_KEY no configurada');
      return null;
    }
    
    console.log('🔔 [FCM] Obteniendo token...');
    const token = await getToken(msg, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: registration
    });
    
    if (token) {
      console.log('✅ [FCM] Token FCM obtenido:', token.substring(0, 30) + '...');
      // Guardar token en Firestore
      console.log('🔔 [FCM] Guardando token en Firestore...');
      await saveTokenToFirestore(userId, businessId, token);
      console.log('✅ [FCM] Token guardado exitosamente');
      return token;
    } else {
      console.warn('⚠️ [FCM] No se pudo obtener token FCM (token es null)');
      return null;
    }
  } catch (error: any) {
    console.error('❌ [FCM] Error solicitando permiso de notificación:', error);
    console.error('❌ [FCM] Error message:', error.message);
    console.error('❌ [FCM] Error code:', error.code);
    return null;
  }
};

/**
 * Guarda el token FCM en Firestore
 */
const saveTokenToFirestore = async (userId: string, businessId: string, token: string): Promise<void> => {
  console.log('🔔 [FCM-Save] Iniciando guardado de token...', { userId, businessId, tokenPrefix: token.substring(0, 20) });
  
  try {
    // Guardar en el documento del usuario
    const userRef = doc(db, 'users', userId);
    console.log('🔔 [FCM-Save] Verificando usuario...');
    const userDoc = await getDoc(userRef);
    
    if (userDoc.exists()) {
      console.log('🔔 [FCM-Save] Usuario existe, actualizando...');
      await updateDoc(userRef, {
        fcmTokens: arrayUnion(token),
        pushNotificationsEnabled: true,
        updatedAt: new Date()
      });
      console.log('✅ [FCM-Save] Usuario actualizado');
    } else {
      console.log('🔔 [FCM-Save] Usuario no existe, creando con merge...');
      await setDoc(userRef, {
        fcmTokens: [token],
        pushNotificationsEnabled: true,
        updatedAt: new Date()
      }, { merge: true });
      console.log('✅ [FCM-Save] Usuario creado/mergeado');
    }
    
    // También guardar en una colección separada para facilitar el envío de notificaciones
    console.log('🔔 [FCM-Save] Guardando en colección fcmTokens...');
    const tokenRef = doc(db, 'fcmTokens', token.substring(0, 50)); // Usar prefijo como ID
    await setDoc(tokenRef, {
      userId,
      businessId,
      token,
      createdAt: new Date(),
      platform: detectPlatform(),
      userAgent: navigator.userAgent
    });
    
    console.log('✅ [FCM-Save] Token FCM guardado en Firestore exitosamente');
  } catch (error: any) {
    console.error('❌ [FCM-Save] Error guardando token FCM:', error);
    console.error('❌ [FCM-Save] Error message:', error.message);
    console.error('❌ [FCM-Save] Error code:', error.code);
    throw error; // Re-throw para que el llamador sepa que falló
  }
};

/**
 * Elimina el token FCM cuando el usuario desactiva notificaciones
 */
export const removeNotificationToken = async (userId: string, token: string): Promise<void> => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      fcmTokens: arrayRemove(token),
      pushNotificationsEnabled: false,
      updatedAt: new Date()
    });
    
    // Eliminar de la colección de tokens
    const tokenRef = doc(db, 'fcmTokens', token);
    await setDoc(tokenRef, { deleted: true, deletedAt: new Date() }, { merge: true });
    
    console.log('✅ Token FCM eliminado');
  } catch (error) {
    console.error('❌ Error eliminando token FCM:', error);
  }
};

/**
 * Configura el listener para mensajes en primer plano
 */
export const setupForegroundMessageListener = (callback: (payload: any) => void): (() => void) | null => {
  if (!messaging) {
    initializeMessaging().then(msg => {
      if (msg) {
        onMessage(msg, (payload) => {
          console.log('📬 Mensaje recibido en primer plano:', payload);
          callback(payload);
        });
      }
    });
    return null;
  }
  
  return onMessage(messaging, (payload) => {
    console.log('📬 Mensaje recibido en primer plano:', payload);
    callback(payload);
  });
};

/**
 * Detecta la plataforma del usuario
 */
const detectPlatform = (): string => {
  const ua = navigator.userAgent;
  if (/android/i.test(ua)) return 'android';
  if (/iPad|iPhone|iPod/.test(ua)) return 'ios';
  if (/Windows/.test(ua)) return 'windows';
  if (/Mac/.test(ua)) return 'macos';
  if (/Linux/.test(ua)) return 'linux';
  return 'unknown';
};

/**
 * Verifica si la app está instalada como PWA
 */
export const isPWAInstalled = (): boolean => {
  // Verificar display-mode standalone
  if (window.matchMedia('(display-mode: standalone)').matches) {
    return true;
  }
  
  // Para iOS
  if ((navigator as any).standalone === true) {
    return true;
  }
  
  return false;
};

/**
 * Muestra una notificación local (para testing o fallback)
 */
export const showLocalNotification = async (title: string, options: NotificationOptions = {}): Promise<void> => {
  if (!('Notification' in window)) return;
  
  if (Notification.permission === 'granted') {
    const registration = await navigator.serviceWorker.ready;
    await registration.showNotification(title, {
      icon: '/icons/icon-192x192.png',
      badge: '/icons/notification-badge-v6.png',
      vibrate: [100, 50, 100],
      ...options
    });
  }
};

